import { ApiError, fetchApi } from '@/api/client';
import { inOrder, optimistic, scheduleInvalidation, tempId } from '@/api/optimistic';
import { queryKeys } from '@/api/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TripItemConflict, tripListKey } from '@ultimate-checklist/shared';
import type {
  AssignTripItemsInput,
  BulkCreateTripItemsInput,
  BulkCreateTripItemsOutput,
  BulkRemoveTripItemsInput,
  BulkUpdateTripItemsInput,
  CreateTripInput,
  CreateTripInviteInput,
  CreateTripItemCommentInput,
  CreateTripItemInput,
  CreateTripItemLabelInput,
  DeleteTripItemLabelInput,
  FindTripDuplicatesInput,
  FindTripDuplicatesResult,
  IncomingTripInvite,
  Me,
  MoveTripItemsInput,
  PresenceInput,
  PromoteTripListInput,
  PromoteTripListResult,
  PullIntoTripInput,
  QuickAddInput,
  QuickAddResult,
  ReconcileResult,
  ReconcileTripListInput,
  RenameTripListInput,
  ReorderTripItemsInput,
  Trip,
  TripCollaboratorWithUser,
  TripInvite,
  TripItemComment,
  TripItemEvent,
  TripItemWithLabels,
  TripListTarget,
  TripMember,
  UpdateTripCollaboratorInput,
  UpdateTripInput,
  UpdateTripItemCommentInput,
  UpdateTripItemFlagInput,
  UpdateTripItemInput,
  UpdateTripItemQuestionInput,
  UpdateTripItemStatusInput,
  UserSummary,
} from '@ultimate-checklist/shared';

export function useTrips(status?: Trip['status']) {
  const search = status ? `?status=${status}` : '';
  return useQuery({
    queryKey: queryKeys.trips.list(status),
    queryFn: ({ signal }) => fetchApi<Trip[]>(`/api/trips${search}`, { signal }),
  });
}

export function useTrip(tripId: string) {
  return useQuery({
    queryKey: queryKeys.trips.detail(tripId),
    queryFn: ({ signal }) => fetchApi<Trip>(`/api/trips/${tripId}`, { signal }),
  });
}

export function useTripItems(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.items(tripId),
    queryFn: ({ signal }) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items`, { signal }),
    enabled,
    // Deliberately no background refresh: other people's edits are detected
    // (useRemoteTripChanges) and shown as a dot, and only applied when the user
    // asks — the board never changes under their thumb.
    refetchOnWindowFocus: false,
  });
}

export function useTripLabels(tripId: string) {
  return useQuery({
    queryKey: queryKeys.trips.labels(tripId),
    queryFn: ({ signal }) => fetchApi<string[]>(`/api/trips/${tripId}/labels`, { signal }),
  });
}

export function useTripQuestions(tripId: string) {
  return useQuery({
    queryKey: queryKeys.trips.questions(tripId),
    queryFn: ({ signal }) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/questions`, { signal }),
    refetchOnWindowFocus: true,
    // Gentle background sync for collaborators; optimistic updates carry the
    // local snappiness, so we don't need an aggressive poll fighting them.
    refetchInterval: 30_000,
  });
}

export function useTripCollaborators(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.collaborators(tripId),
    queryFn: ({ signal }) =>
      fetchApi<TripCollaboratorWithUser[]>(`/api/trips/${tripId}/collaborators`, { signal }),
    enabled,
  });
}

/** Activity feed for a single trip item (read-only). Fetches when an item is open. */
export function useTripItemEvents(tripId: string, itemId: string | null) {
  return useQuery({
    queryKey: queryKeys.trips.itemEvents(tripId, itemId ?? '__none__'),
    queryFn: ({ signal }) =>
      fetchApi<TripItemEvent[]>(`/api/trips/${tripId}/items/${itemId}/events`, { signal }),
    enabled: itemId !== null,
  });
}

export function useTripEvents(tripId: string) {
  return useQuery({
    queryKey: queryKeys.trips.events(tripId),
    queryFn: ({ signal }) => fetchApi<TripItemEvent[]>(`/api/trips/${tripId}/events`, { signal }),
    refetchOnWindowFocus: true,
  });
}

/** The trip's removed (soft-deleted) items — the archive behind "Restore". */
export function useRemovedTripItems(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.removedItems(tripId),
    queryFn: ({ signal }) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/removed`, { signal }),
    enabled,
  });
}

export function useRestoreTripItem(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (itemId: string) =>
      fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/restore`, {
        method: 'POST',
      }),
    ),
    onSuccess: (restored) => {
      qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.removedItems(tripId), (current) =>
        (current ?? []).filter((item) => item.id !== restored.id),
      );
      qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
        current ? [...current, restored] : current,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

/** Everyone an item can be handed to: the owner plus collaborators. */
export function useTripMembers(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.members(tripId),
    queryFn: ({ signal }) => fetchApi<TripMember[]>(`/api/trips/${tripId}/members`, { signal }),
    enabled,
  });
}

/** Hand several items to one member (or clear them) in one call. */
export function useAssignTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: AssignTripItemsInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/assign`, {
        method: 'POST',
        body,
      }),
    ),
    ...optimistic<AssignTripItemsInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemIds, handledBy }) => {
        const ids = new Set(itemIds);
        const member = (qc.getQueryData<TripMember[]>(queryKeys.trips.members(tripId)) ?? []).find(
          (candidate) => candidate.id === handledBy,
        );
        const handledByUser = member
          ? { id: member.id, email: member.email, displayName: member.displayName }
          : null;
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (items) =>
          (items ?? []).map((item) =>
            ids.has(item.id) ? { ...item, handledBy, handledByUser } : item,
          ),
        );
      },
      errorMessage: 'Couldn’t assign those items',
      also: () => [queryKeys.trips.all, queryKeys.trips.events(tripId)],
    }),
  });
}

/** Rename a pulled list on this trip (library link kept). */
export function useRenameTripList(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: RenameTripListInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/lists/rename`, {
        method: 'POST',
        body,
      }),
    ),
    ...optimistic<RenameTripListInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ originMasterListId, name }) =>
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (items) =>
          (items ?? []).map((item) =>
            item.originMasterListId === originMasterListId
              ? { ...item, originMasterListName: name }
              : item,
          ),
        ),
      errorMessage: 'Couldn’t rename that list',
      also: () => [
        queryKeys.trips.all,
        queryKeys.trips.events(tripId),
        queryKeys.trips.removedItems(tripId),
      ],
    }),
  });
}

/** Bulk pack / unpack / flag / unflag — the selection bar. */
export function useBulkUpdateTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: BulkUpdateTripItemsInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/bulk-update`, {
        method: 'POST',
        body,
      }),
    ),
    ...optimistic<BulkUpdateTripItemsInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemIds, patch }) => {
        const ids = new Set(itemIds);
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (items) =>
          (items ?? []).map((item) =>
            ids.has(item.id)
              ? {
                  ...item,
                  ...(patch.status !== undefined
                    ? {
                        status: patch.status,
                        finalChecked: patch.status === 'todo' ? false : item.finalChecked,
                      }
                    : {}),
                  ...(patch.flagged !== undefined ? { flagged: patch.flagged } : {}),
                }
              : item,
          ),
        );
      },
      errorMessage: 'Couldn’t update those items',
      also: () => [queryKeys.trips.all, queryKeys.trips.events(tripId)],
    }),
  });
}

/** Bulk remove (soft delete — restorable from Manage → Removed items). */
export function useBulkRemoveTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: BulkRemoveTripItemsInput) =>
      fetchApi<void>(`/api/trips/${tripId}/items/bulk-remove`, { method: 'POST', body }),
    ),
    ...optimistic<BulkRemoveTripItemsInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemIds }) => {
        const ids = new Set(itemIds);
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (items) =>
          (items ?? []).filter((item) => !ids.has(item.id)),
        );
      },
      errorMessage: 'Couldn’t remove those items',
      also: () => [
        queryKeys.trips.all,
        queryKeys.trips.events(tripId),
        queryKeys.trips.removedItems(tripId),
      ],
    }),
  });
}

/** Every comment on the trip (live items), for the AI brief. */
export function useTripComments(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.comments(tripId),
    queryFn: ({ signal }) =>
      fetchApi<TripItemComment[]>(`/api/trips/${tripId}/comments`, { signal }),
    enabled,
  });
}

export function useTripItemComments(tripId: string, itemId: string | null) {
  return useQuery({
    queryKey: queryKeys.trips.itemComments(tripId, itemId ?? '__none__'),
    queryFn: ({ signal }) =>
      fetchApi<TripItemComment[]>(`/api/trips/${tripId}/items/${itemId}/comments`, { signal }),
    enabled: itemId !== null,
  });
}

export function useCreateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTripInput) => fetchApi<Trip>('/api/trips', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

export function useUpdateTrip(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: UpdateTripInput) =>
      fetchApi<Trip>(`/api/trips/${tripId}`, { method: 'PATCH', body }),
    ),
    ...optimistic<UpdateTripInput>({
      qc,
      keys: () => [queryKeys.trips.detail(tripId)],
      apply: (body) =>
        qc.setQueryData<Trip>(queryKeys.trips.detail(tripId), (current) =>
          current ? ({ ...current, ...body } as Trip) : current,
        ),
      errorMessage: 'Couldn’t rename the trip',
      also: () => [queryKeys.trips.all],
    }),
  });
}

export function useCloseTrip(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, () =>
      fetchApi<Trip>(`/api/trips/${tripId}/close`, { method: 'POST' }),
    ),
    onSuccess: (trip) => {
      qc.setQueryData(queryKeys.trips.detail(tripId), trip);
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

export function useReopenTrip(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, () =>
      fetchApi<Trip>(`/api/trips/${tripId}/reopen`, { method: 'POST' }),
    ),
    onSuccess: (trip) => {
      qc.setQueryData(queryKeys.trips.detail(tripId), trip);
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

export function usePullIntoTrip(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: PullIntoTripInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/pull-in`, { method: 'POST', body }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

/** Pending invites for a trip (owner view). */
export function useTripInvites(tripId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.trips.invites(tripId),
    queryFn: ({ signal }) => fetchApi<TripInvite[]>(`/api/trips/${tripId}/invites`, { signal }),
    enabled,
  });
}

/** Invite someone by email — creates a pending invite (no instant access). */
export function useCreateTripInvite(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: CreateTripInviteInput) =>
      fetchApi<TripInvite>(`/api/trips/${tripId}/invites`, { method: 'POST', body }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.invites(tripId) });
    },
  });
}

/** Owner cancels a pending invite. */
export function useCancelTripInvite(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (inviteId: string) =>
      fetchApi<void>(`/api/trips/${tripId}/invites/${inviteId}`, { method: 'DELETE' }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.invites(tripId) });
    },
  });
}

/** The signed-in user's incoming invites (Trips home screen). */
export function useMyInvites() {
  return useQuery({
    queryKey: queryKeys.trips.incomingInvites,
    queryFn: ({ signal }) => fetchApi<IncomingTripInvite[]>('/api/invites', { signal }),
  });
}

/** Accept an incoming invite → become a collaborator with access. */
export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      fetchApi<Trip>(`/api/invites/${inviteId}/accept`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.incomingInvites });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

/** Decline an incoming invite. */
export function useDeclineInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      fetchApi<void>(`/api/invites/${inviteId}/decline`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.incomingInvites });
    },
  });
}

export function useUpdateTripCollaborator(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({
        collaboratorId,
        body,
      }: {
        collaboratorId: string;
        body: UpdateTripCollaboratorInput;
      }) =>
        fetchApi<TripCollaboratorWithUser>(`/api/trips/${tripId}/collaborators/${collaboratorId}`, {
          method: 'PATCH',
          body,
        }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.collaborators(tripId) });
    },
  });
}

export function useDeleteTripCollaborator(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (collaboratorId: string) =>
      fetchApi<void>(`/api/trips/${tripId}/collaborators/${collaboratorId}`, {
        method: 'DELETE',
      }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.collaborators(tripId) });
    },
  });
}

/** Optimistically patch one item in the cached trip-items list. */
function patchTripItem(
  qc: ReturnType<typeof useQueryClient>,
  tripId: string,
  itemId: string,
  patch: (item: TripItemWithLabels) => TripItemWithLabels,
) {
  qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
    (current ?? []).map((item) => (item.id === itemId ? patch(item) : item)),
  );
}

function userSummaryFromMe(me: Me | undefined): UserSummary | null {
  if (!me) {
    return null;
  }
  return {
    id: me.id,
    email: me.email,
    displayName: me.displayName,
  };
}

export function useAddTripItem(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: CreateTripItemInput) =>
      fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items`, { method: 'POST', body }),
    ),
    ...optimistic<CreateTripItemInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: (body) =>
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) => {
          const items = current ?? [];
          // Snapshot the origin name from a sibling item already in that list;
          // a bare name (no id) targets a trip-local list and is kept as-is.
          const originName = body.originMasterListId
            ? (items.find((i) => i.originMasterListId === body.originMasterListId)
                ?.originMasterListName ?? null)
            : (body.originMasterListName ?? null);
          const currentUser = userSummaryFromMe(qc.getQueryData<Me>(queryKeys.me));
          const handledBy =
            body.handledBy === undefined ? (currentUser?.id ?? null) : body.handledBy;
          const placeholder: TripItemWithLabels = {
            id: tempId(),
            tripId,
            originMasterListId: body.originMasterListId ?? null,
            originMasterListName: originName,
            originMasterListItemId: body.originMasterListId
              ? (body.originMasterListItemId ?? null)
              : null,
            controlledByItemId: null,
            name: body.name,
            description: body.description ?? null,
            status: 'todo',
            finalChecked: false,
            flagged: false,
            questionOpen: false,
            questionText: null,
            forPersonId: body.forPersonId ?? null,
            handledBy,
            handledByUser: handledBy && currentUser?.id === handledBy ? currentUser : null,
            note: body.note ?? null,
            labels: [],
            position: items.length,
            createdBy: currentUser?.id ?? '',
            createdByUser: currentUser,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            deletedAt: null,
            deletedBy: null,
            commentCount: 0,
            lastCommentBy: null,
          };
          return [...items, placeholder];
        }),
      errorMessage: 'Couldn’t add that item',
      also: () => [queryKeys.trips.all, queryKeys.trips.events(tripId)],
    }),
  });
}

export function useUpdateTripItem(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, ({ itemId, body }: { itemId: string; body: UpdateTripItemInput }) =>
      fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}`, {
        method: 'PATCH',
        body,
      }),
    ),
    ...optimistic<{ itemId: string; body: UpdateTripItemInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) => {
        const currentUser = userSummaryFromMe(qc.getQueryData<Me>(queryKeys.me));
        patchTripItem(qc, tripId, itemId, (item) => {
          const next = { ...item, ...body };
          if (body.handledBy !== undefined) {
            next.handledByUser =
              body.handledBy && currentUser?.id === body.handledBy ? currentUser : null;
          }
          return next;
        });
      },
      errorMessage: 'Couldn’t save that change',
      also: ({ itemId }) => [
        queryKeys.trips.all,
        queryKeys.trips.events(tripId),
        queryKeys.trips.itemEvents(tripId, itemId),
      ],
      // A compare-and-set miss (someone else edited the same text first): show
      // their version on the board at once; the editor resolves it inline.
      handleError: (error, { itemId }) => {
        if (!(error instanceof ApiError) || error.code !== 'CONFLICT') {
          return false;
        }
        const conflict = TripItemConflict.safeParse(error.details);
        if (conflict.success) {
          patchTripItem(qc, tripId, itemId, (item) => ({ ...item, ...conflict.data.current }));
        }
        return true;
      },
    }),
  });
}

/** Tell the live stream which item this tab has open (see useLiveTrip). */
export function useReportPresence(tripId: string) {
  return useMutation({
    mutationFn: (body: PresenceInput) =>
      fetchApi<void>(`/api/trips/${tripId}/presence`, { method: 'POST', body }),
  });
}

export function useDeleteTripItem(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (itemId: string) =>
      fetchApi<void>(`/api/trips/${tripId}/items/${itemId}`, { method: 'DELETE' }),
    ),
    ...optimistic<string>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: (itemId) =>
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
          (current ?? []).filter((item) => item.id !== itemId),
        ),
      errorMessage: 'Couldn’t remove that item',
      also: () => [
        queryKeys.trips.all,
        queryKeys.trips.events(tripId),
        queryKeys.trips.removedItems(tripId),
      ],
    }),
  });
}

/** Remove a whole list from the trip in one request — a distinct operation from
 * deleting items (one transaction, one `list_removed` audit event). The target
 * addresses a pulled list (origin id), a trip-local list (name), or the manual
 * bucket (both null). */
export function useRemoveTripList(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (target: TripListTarget) =>
      fetchApi<void>(`/api/trips/${tripId}/lists/remove`, {
        method: 'POST',
        body: target,
      }),
    ),
    ...optimistic<TripListTarget>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: (target) => {
        const targetKey = tripListKey(target);
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
          (current ?? []).filter((item) => tripListKey(item) !== targetKey),
        );
      },
      errorMessage: 'Couldn’t remove that list — try again',
      also: () => [queryKeys.trips.all, queryKeys.trips.events(tripId)],
    }),
  });
}

/** Move items into another list on this trip (creating a trip-local list when the
 * target is a bare name). Status, labels, comments, and history ride along. */
export function useMoveTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: MoveTripItemsInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/move`, {
        method: 'POST',
        body,
      }),
    ),
    ...optimistic<MoveTripItemsInput>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemIds, target }) => {
        const ids = new Set(itemIds);
        // A bare-name target keeps the name; an origin-id target's snapshot name
        // is resolved from a sibling item already in that list.
        const current = qc.getQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId)) ?? [];
        const originName = target.originMasterListId
          ? (current.find((item) => item.originMasterListId === target.originMasterListId)
              ?.originMasterListName ?? null)
          : (target.originMasterListName ?? null);
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (items) =>
          (items ?? []).map((item) =>
            ids.has(item.id)
              ? {
                  ...item,
                  originMasterListId: target.originMasterListId,
                  originMasterListName: originName,
                  originMasterListItemId: null,
                }
              : item,
          ),
        );
      },
      errorMessage: 'Couldn’t move those items — try again',
      also: () => [queryKeys.trips.all, queryKeys.trips.events(tripId)],
    }),
  });
}

/** Push a trip-local list up to the Library (new master list + linked items). */
export function usePromoteTripList(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: PromoteTripListInput) =>
      fetchApi<PromoteTripListResult>(`/api/trips/${tripId}/lists/promote`, {
        method: 'POST',
        body,
      }),
    ),
    onSuccess: (result) => {
      qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) => {
        const byId = new Map(result.items.map((item) => [item.id, item]));
        return (current ?? []).map((item) => byId.get(item.id) ?? item);
      });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.masterLists.all });
    },
  });
}

export function useCreateTripItemComment(tripId: string, itemId: string) {
  const qc = useQueryClient();
  const commentsKey = queryKeys.trips.itemComments(tripId, itemId);
  return useMutation({
    mutationFn: inOrder(tripId, (body: CreateTripItemCommentInput) =>
      fetchApi<TripItemComment>(`/api/trips/${tripId}/items/${itemId}/comments`, {
        method: 'POST',
        body,
      }),
    ),
    onSuccess: (comment) => {
      qc.setQueryData<TripItemComment[]>(commentsKey, (current) => [...(current ?? []), comment]);
      // The checklist row's 💬 indicator reads these off the item; update them in
      // place so it reflects the new comment before the items refetch lands.
      qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
        current?.map((item) =>
          item.id === itemId
            ? { ...item, commentCount: item.commentCount + 1, lastCommentBy: comment.createdBy }
            : item,
        ),
      );
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.itemEvents(tripId, itemId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

export function useUpdateTripItemComment(tripId: string, itemId: string) {
  const qc = useQueryClient();
  const commentsKey = queryKeys.trips.itemComments(tripId, itemId);
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({
        commentId,
        body,
      }: {
        commentId: string;
        body: UpdateTripItemCommentInput;
      }) =>
        fetchApi<TripItemComment>(`/api/trips/${tripId}/items/${itemId}/comments/${commentId}`, {
          method: 'PATCH',
          body,
        }),
    ),
    onSuccess: (comment) => {
      qc.setQueryData<TripItemComment[]>(commentsKey, (current) =>
        (current ?? []).map((existing) => (existing.id === comment.id ? comment : existing)),
      );
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.itemEvents(tripId, itemId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
    },
  });
}

export function useDeleteTripItemComment(tripId: string, itemId: string) {
  const qc = useQueryClient();
  const commentsKey = queryKeys.trips.itemComments(tripId, itemId);
  return useMutation({
    mutationFn: inOrder(tripId, (commentId: string) =>
      fetchApi<void>(`/api/trips/${tripId}/items/${itemId}/comments/${commentId}`, {
        method: 'DELETE',
      }),
    ),
    onSuccess: (_unused, commentId) => {
      const remaining = (qc.getQueryData<TripItemComment[]>(commentsKey) ?? []).filter(
        (comment) => comment.id !== commentId,
      );
      qc.setQueryData<TripItemComment[]>(commentsKey, remaining);
      // Keep the row's 💬 indicator honest: the last commenter is now whoever wrote
      // the newest remaining comment (the thread is stored oldest → newest).
      qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) =>
        current?.map((item) =>
          item.id === itemId
            ? {
                ...item,
                commentCount: Math.max(0, item.commentCount - 1),
                lastCommentBy: remaining.at(-1)?.createdBy ?? null,
              }
            : item,
        ),
      );
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.itemEvents(tripId, itemId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

/** AI audit: diff a trip's list against its source master list (listId). */
export function useReconcileTripList(tripId: string) {
  return useMutation({
    mutationFn: ({ listId, model }: { listId: string; model: ReconcileTripListInput['model'] }) =>
      fetchApi<ReconcileResult>(`/api/trips/${tripId}/reconcile/${listId}`, {
        method: 'POST',
        body: { model },
      }),
  });
}

/** AI scan: cluster same-thing items across different lists in this trip. */
export function useFindTripDuplicates(tripId: string) {
  return useMutation({
    mutationFn: ({ model }: { model: FindTripDuplicatesInput['model'] }) =>
      fetchApi<FindTripDuplicatesResult>(`/api/trips/${tripId}/find-duplicates`, {
        method: 'POST',
        body: { model },
      }),
  });
}

/** AI Quick Add: interpret a freeform instruction into proposed items + lists. */
export function useInterpretQuickAdd(tripId: string) {
  return useMutation({
    mutationFn: ({ instruction, model }: { instruction: string; model: QuickAddInput['model'] }) =>
      fetchApi<QuickAddResult>(`/api/trips/${tripId}/quick-add`, {
        method: 'POST',
        body: { instruction, model },
      }),
  });
}

/** Voice Quick Add: a base64 WAV clip → transcript + routed proposals (read-only). */
export function useInterpretQuickAddVoice(tripId: string) {
  return useMutation({
    mutationFn: (audioBase64: string) =>
      fetchApi<QuickAddResult>(`/api/trips/${tripId}/quick-add/voice`, {
        method: 'POST',
        body: { audioBase64 },
      }),
  });
}

/** Commit several reviewed items in one atomic call (the Quick Add write). */
export function useBulkAddTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (body: BulkCreateTripItemsInput) =>
      fetchApi<BulkCreateTripItemsOutput>(`/api/trips/${tripId}/items/bulk`, {
        method: 'POST',
        body,
      }),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
  });
}

/** Link leaf items to a controller so their status stays in sync. */
export function useLinkTripItems(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ controllerId, linkedItemIds }: { controllerId: string; linkedItemIds: string[] }) =>
        fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/${controllerId}/link`, {
          method: 'POST',
          body: { linkedItemIds },
        }),
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) }),
  });
}

/** Unlink an item (or dissolve its group if it's the controller). */
export function useUnlinkTripItem(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (itemId: string) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/${itemId}/link`, {
        method: 'DELETE',
      }),
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.trips.items(tripId) }),
  });
}

export function useReorderTripItems(tripId: string) {
  const qc = useQueryClient();
  const itemsKey = queryKeys.trips.items(tripId);
  return useMutation({
    mutationFn: inOrder(tripId, (body: ReorderTripItemsInput) =>
      fetchApi<TripItemWithLabels[]>(`/api/trips/${tripId}/items/reorder`, {
        method: 'PATCH',
        body,
      }),
    ),
    onMutate: async ({ itemIds }) => {
      await qc.cancelQueries({ queryKey: itemsKey });
      const previous = qc.getQueryData<TripItemWithLabels[]>(itemsKey) ?? [];
      const byId = new Map(previous.map((item) => [item.id, item]));
      const reordered = itemIds
        .map((id, index) => {
          const item = byId.get(id);
          return item ? { ...item, position: index } : undefined;
        })
        .filter((item): item is TripItemWithLabels => item !== undefined);
      qc.setQueryData<TripItemWithLabels[]>(itemsKey, reordered);
      return { previous };
    },
    onError: (_err, _body, ctx) => {
      if (ctx) {
        qc.setQueryData(itemsKey, ctx.previous);
      }
    },
    onSuccess: (serverItems) => {
      qc.setQueryData(itemsKey, serverItems);
      void qc.invalidateQueries({ queryKey: queryKeys.trips.questions(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.events(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
    onSettled: () => {
      scheduleInvalidation(qc, [itemsKey]);
    },
  });
}

const executionKeys = (tripId: string) => [
  queryKeys.trips.labels(tripId),
  queryKeys.trips.questions(tripId),
  queryKeys.trips.events(tripId),
  queryKeys.trips.all,
];

export function useAddTripItemLabel(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, body }: { itemId: string; body: CreateTripItemLabelInput }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/labels`, {
          method: 'POST',
          body,
        }),
    ),
    ...optimistic<{ itemId: string; body: CreateTripItemLabelInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) =>
        patchTripItem(qc, tripId, itemId, (item) =>
          item.labels.includes(body.label)
            ? item
            : { ...item, labels: [...item.labels, body.label] },
        ),
      errorMessage: 'Couldn’t add that label',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

export function useDeleteTripItemLabel(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, body }: { itemId: string; body: DeleteTripItemLabelInput }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/labels`, {
          method: 'DELETE',
          body,
        }),
    ),
    ...optimistic<{ itemId: string; body: DeleteTripItemLabelInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) =>
        patchTripItem(qc, tripId, itemId, (item) => ({
          ...item,
          labels: item.labels.filter((label) => label !== body.label),
        })),
      errorMessage: 'Couldn’t remove that label',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

export function useUpdateTripItemStatus(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, body }: { itemId: string; body: UpdateTripItemStatusInput }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/status`, {
          method: 'PATCH',
          body,
        }),
    ),
    // Flip the item's status in the cache immediately so checking feels instant.
    // Linked items move as a group: resolve the controller (root) and flip it +
    // all its leaves, mirroring the server cascade.
    ...optimistic<{ itemId: string; body: UpdateTripItemStatusInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) =>
        qc.setQueryData<TripItemWithLabels[]>(queryKeys.trips.items(tripId), (current) => {
          const items = current ?? [];
          const clicked = items.find((item) => item.id === itemId);
          const rootId = clicked?.controlledByItemId ?? itemId;
          return items.map((item) =>
            item.id === rootId || item.controlledByItemId === rootId
              ? {
                  ...item,
                  status: body.status,
                  finalChecked: body.status === 'done' ? item.finalChecked : false,
                }
              : item,
          );
        }),
      errorMessage: 'Couldn’t update that item — try again',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

/** "Final check" toggle — confirm an item is physically packed (implies done). */
export function useUpdateTripItemFinal(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, finalChecked }: { itemId: string; finalChecked: boolean }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/final`, {
          method: 'PATCH',
          body: { finalChecked },
        }),
    ),
    ...optimistic<{ itemId: string; finalChecked: boolean }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, finalChecked }) =>
        patchTripItem(qc, tripId, itemId, (item) => ({
          ...item,
          finalChecked,
          status: finalChecked ? 'done' : item.status,
        })),
      errorMessage: 'Couldn’t update that item — try again',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

export function useUpdateTripItemFlag(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, body }: { itemId: string; body: UpdateTripItemFlagInput }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/flag`, {
          method: 'PATCH',
          body,
        }),
    ),
    ...optimistic<{ itemId: string; body: UpdateTripItemFlagInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) =>
        patchTripItem(qc, tripId, itemId, (item) => ({ ...item, flagged: body.flagged })),
      errorMessage: 'Couldn’t update that flag',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

export function useMarkTripItemBought(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(tripId, (itemId: string) =>
      fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/bought`, {
        method: 'POST',
      }),
    ),
    ...optimistic<string>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: (itemId) => patchTripItem(qc, tripId, itemId, (item) => ({ ...item, status: 'done' })),
      errorMessage: 'Couldn’t mark that bought',
      also: (itemId) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}

export function useUpdateTripItemQuestion(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inOrder(
      tripId,
      ({ itemId, body }: { itemId: string; body: UpdateTripItemQuestionInput }) =>
        fetchApi<TripItemWithLabels>(`/api/trips/${tripId}/items/${itemId}/question`, {
          method: 'PATCH',
          body,
        }),
    ),
    ...optimistic<{ itemId: string; body: UpdateTripItemQuestionInput }>({
      qc,
      keys: () => [queryKeys.trips.items(tripId)],
      apply: ({ itemId, body }) =>
        patchTripItem(qc, tripId, itemId, (item) => ({
          ...item,
          questionOpen: body.questionOpen,
          questionText: body.questionText ?? null,
        })),
      errorMessage: 'Couldn’t save that question',
      also: ({ itemId }) => [...executionKeys(tripId), queryKeys.trips.itemEvents(tripId, itemId)],
    }),
  });
}
